import { Link } from 'react-router-dom';
import {
  FiShield,
  FiCpu,
  FiRefreshCw,
  FiLayers,
  FiMap,
  FiArrowRight,
  FiUsers,
  FiBarChart2,
  FiFileText,
  FiZap,
} from 'react-icons/fi';

import {
  Seo,
  Section,
  SectionHeader,
  Card,
  Button,
  CtaBanner,
  GableMark,
  LeadershipPortrait,
} from '../components/ui';
import { capabilities, services, integratedApproach } from '../data/capabilities';
import { engagements, differentiators } from '../data/pastPerformance';
import { founder, philosophy } from '../data/about';
import { company } from '../data/company';

import { images } from '../data/images';
import './home.css';

/* Same six icons as the About page — the differentiators are the same content
   and should not be presented two different ways. */
const DIFFERENTIATOR_ICONS = [FiUsers, FiBarChart2, FiShield, FiRefreshCw, FiFileText, FiZap];

const CAPABILITY_ICONS = {
  'ai-governance-quantum-ready-cybersecurity': FiCpu,
  'cybersecurity-solutions': FiShield,
  'change-and-it-service-management': FiRefreshCw,
  'enterprise-services': FiLayers,
  lscnexus: FiMap,
};

/* Every figure below is derived from the content, so none of it can drift out
   of date and none of it is a marketing claim we cannot substantiate. */
const serviceLineCount = capabilities.reduce(
  (n, c) => n + c.groups.reduce((m, g) => m + g.items.length, 0),
  0
);

const CREDENTIALS = [
  { value: String(services.length), label: 'Service areas' },
  { value: `${serviceLineCount}`, label: 'Service lines delivered' },
  { value: String(company.frameworks.length), label: 'Frameworks we are measured on' },
  { value: String(engagements.length), label: 'Federal programs supported' },
];

const Home = () => (
  <>
    <Seo
      title="Cybersecurity, AI Governance & Quantum Readiness for Federal Missions"
      description="Bethel Innovations LLC is a small business delivering cybersecurity, AI governance, post-quantum readiness and enterprise IT services to federal agencies, prime contractors and nonprofit organizations."
      path="/"
    />

    {/* ================= HERO ================= */}
    <section
      className="hero on-navy"
      style={{ backgroundImage: `url(${images.heroExterior})` }}
      aria-labelledby="hero-title"
    >
      <GableMark className="hero__motif" />
      <div className="container hero__inner">
        <p className="eyebrow">Small Business</p>
        <h1 id="hero-title" className="hero__title">
          Secure the mission. Govern the technology. Prepare for what is next.
        </h1>
        <p className="hero__lead">
          Bethel Innovations delivers cybersecurity, AI governance and post-quantum
          readiness to federal agencies, prime contractors and nonprofit
          organizations — connecting governance, security and engineering into one
          mission-focused strategy.
        </p>
        <div className="hero__actions">
          <Button to="/capabilities" variant="invert" size="lg" icon={<FiArrowRight />}>
            Explore Capabilities
          </Button>
          <Button to="/capability-statement" variant="ghost-invert" size="lg">
            Capability Statement
          </Button>
        </div>
      </div>
    </section>

    {/* ================= CREDENTIALS BAND =================
        Deliberately overlaps the hero's lower edge. The page was reading as a
        stack of full-width slabs; this is the first element that crosses a
        section boundary and gives the composition depth. */}
    <div className="credentials-band">
      <div className="container">
        <dl className="credentials-band__inner corner-ticks" data-reveal-stagger>
          {CREDENTIALS.map((c) => (
            <div className="credential-stat" key={c.label}>
              <dd className="credential-stat__value tabular">{c.value}</dd>
              <dt className="credential-stat__label">{c.label}</dt>
            </div>
          ))}
        </dl>
      </div>
    </div>

    {/* ================= TRUST BAR ================= */}
    <section className="trust-bar" aria-labelledby="trust-bar-title">
      <div className="container">
        <h2 id="trust-bar-title" className="trust-bar__title">
          Trusted on federal programs at
        </h2>
        <ul className="trust-bar__logos">
          {engagements.map((item) => (
            <li key={item.id}>
              {/* A real anchor — react-router's <Link> treats an absolute URL
                  as an internal path, which is why these were dead before. */}
              <a
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${item.client} (opens in a new tab)`}
              >
                <img
                  src={item.logo}
                  alt={item.logoAlt}
                  loading="lazy"
                  style={{ '--logo-scale': item.logoScale ?? 1 }}
                />
              </a>
            </li>
          ))}
        </ul>
        <p className="trust-bar__note">
          <Link to="/past-performance" className="link-arrow">
            See what we delivered <FiArrowRight />
          </Link>
        </p>
      </div>
    </section>

    {/* ================= CAPABILITIES ================= */}
    <Section tone="alt" aria-labelledby="capabilities-title">
      <SectionHeader
        id="capabilities-title"
        eyebrow="What we do"
        title="Five services, one integrated practice"
        lead="From full-cycle Security Assessment & Authorization through AI governance and post-quantum migration planning — delivered by practitioners who have done it inside federal programs."
      />
      <div className="grid grid--3" data-reveal-stagger>
        {services.map((service) => {
          const Icon = CAPABILITY_ICONS[service.slug];
          return (
            <Card
              key={service.slug}
              to={service.to}
              icon={<Icon />}
              title={service.title}
              body={service.summary}
            />
          );
        })}
      </div>
    </Section>

    {/* ================= INTEGRATED APPROACH ================= */}
    <Section tone="navy" aria-labelledby="integrated-title">
      <div className="split split--text-wide">
        <div className="split__body">
          <SectionHeader
            id="integrated-title"
            eyebrow="Our difference"
            title={integratedApproach.heading}
          />
          <div className="prose">
            {integratedApproach.body.map((para) => (
              <p key={para.slice(0, 40)}>{para}</p>
            ))}
          </div>
          <div>
            <Button to="/capabilities" variant="invert">
              How we work
            </Button>
          </div>
        </div>
        <div className="integrated-facts">
          <p className="integrated-facts__quote">{integratedApproach.closing}</p>
          <ul className="integrated-facts__list">
            {company.frameworks.slice(0, 6).map((f) => (
              <li key={f} className="pill pill--invert">
                {f}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Section>

    {/* ================= LEADERSHIP ================= */}
    <Section aria-labelledby="leadership-title">
      <div className="split">
        <div className="split__media">
          <LeadershipPortrait person={founder} />
        </div>
        <div className="split__body">
          <SectionHeader
            id="leadership-title"
            eyebrow="Leadership"
            title={founder.name}
          />
          <p className="leadership__role">{founder.role}</p>
          <div className="prose">
            <p>{founder.bio[0]}</p>
            <p>{founder.bio[1]}</p>
          </div>
          <div>
            <Button to="/about-us" variant="secondary">
              About Bethel Innovations
            </Button>
          </div>
        </div>
      </div>
    </Section>

    {/* ================= PHILOSOPHY ================= */}
    <Section tone="navy-deep" aria-labelledby="philosophy-title">
      <blockquote className="philosophy corner-ticks">
        <GableMark className="philosophy__motif" />
        <p className="philosophy__eyebrow" id="philosophy-title">
          {philosophy.title}
        </p>
        <p className="philosophy__statement">{philosophy.statement}</p>
        <footer className="philosophy__attribution">
          <cite>{company.legalName}</cite>
        </footer>
      </blockquote>
    </Section>

    {/* ================= WHY CHOOSE US =================
        The previous site presented these six points as an auto-scrolling
        marquee — unreadable when scanning and a WCAG 2.2.2 problem. Static
        grid instead. */}
    <Section tone="alt" aria-labelledby="why-title">
      <SectionHeader
        id="why-title"
        eyebrow="Why choose us"
        title="What a program office actually gets"
        align="center"
      />
      <div className="grid grid--3" data-reveal-stagger>
        {differentiators.map((item, i) => {
          const Icon = DIFFERENTIATOR_ICONS[i];
          return (
            <Card
              key={item.title}
              icon={<Icon />}
              title={item.title}
              body={item.body}
            />
          );
        })}
      </div>
    </Section>

    <CtaBanner
      title="Let us grow your organization"
      body="Tell us about your project, compliance requirements, or operational challenges. Our team will respond within one business day."
      primary={{ label: 'Contact Us', to: '/contact-us' }}
      secondary={{ label: 'Capability Statement', to: '/capability-statement' }}
    />
  </>
);

export default Home;
